import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Inbox, SearchX, UserPlus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AdminPage, EmptyState, PillTabs, RelativeTime, SearchField, TablePagination } from '../../../components/design-system/admin';
import { useAdminSignupsApi } from '../../../common/hooks/use-admin-signups-api.hook';
import { DemoRequest, DemoStatus, SignupDetail, SignupRow, SignupStats, SignupView, demoId } from '../../../common/models/admin-signups.model';
import { useAdminNav } from '../../../modules/superadmin/shell/admin-nav.store';
import { DemoRequestsTable } from '../../../modules/superadmin/signups/DemoRequestsTable';
import { SignupDetailSheet } from '../../../modules/superadmin/signups/SignupDetailSheet';
import { SignupFunnel } from '../../../modules/superadmin/signups/SignupFunnel';
import { SdkPresenceSummary, SignupProgress, SignupStatusBadge, VerdictChip, useSdkCheck } from '../../../modules/superadmin/signups/signup-parts';

type Tab = 'review' | 'all' | 'visitors' | 'demos';
const TABS: Tab[] = ['review', 'all', 'visitors', 'demos'];
const VIEW: Record<Exclude<Tab, 'demos'>, SignupView> = { review: 'review', all: 'all', visitors: 'visitors' };
const STATS_DAYS = 30;
const SEARCH_DEBOUNCE_MS = 300;

const isTab = (v: string | null): v is Tab => !!v && (TABS as string[]).includes(v);

/**
 * Super-admin Signups (docs/plg-review.md → Super-admin): everyone who tried
 * "Start for free" and how far they got, the accounts waiting for a person to
 * approve them, and the demo requests. `?review=<vendorId>` opens one signup
 * (the staff notification links there); `?tab=demos` opens the demo requests.
 */
export function SignupsPage() {
  const { t } = useTranslation();
  const api = useAdminSignupsApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [params, setParams] = useSearchParams();

  const tab: Tab = isTab(params.get('tab')) ? (params.get('tab') as Tab) : 'review';
  const reviewId = params.get('review');

  const [stats, setStats] = useState<SignupStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const [rows, setRows] = useState<SignupRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [demos, setDemos] = useState<DemoRequest[]>([]);
  const [demosTotal, setDemosTotal] = useState(0);
  const [demosLoading, setDemosLoading] = useState(false);

  // A visitor has no account to load: the sheet shows what the list knows.
  const [visitor, setVisitor] = useState<SignupRow | null>(null);
  // The rows whose SDK is being looked for right now.
  const [checking, setChecking] = useState<string[]>([]);
  const runSdkCheck = useSdkCheck();

  const loadStats = useCallback(async () => {
    try {
      const s = await apiRef.current.stats(STATS_DAYS);
      if (s) {
        setStats(s);
        useAdminNav.getState().setSignupsPending?.(s.pendingReview ?? 0);
      }
    } catch {
      /* the strip stays empty; the table still works */
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadRows = useCallback(async () => {
    if (tab === 'demos') return;
    setLoading(true);
    try {
      const res = await apiRef.current.list({ view: VIEW[tab], q: q || undefined, days: undefined, page: page + 1, limit: pageSize });
      setRows(res.rows);
      setTotal(res.total);
    } catch {
      setRows([]);
      setTotal(0);
      toast.error(t('ADMIN_SIGNUPS.TOAST.LOAD_FAILED'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, q, page, pageSize]);

  const loadDemos = useCallback(async () => {
    setDemosLoading(true);
    try {
      const res = await apiRef.current.demoRequests({ page: page + 1, limit: pageSize });
      setDemos(res.rows);
      setDemosTotal(res.total);
    } catch {
      setDemos([]);
      setDemosTotal(0);
      toast.error(t('ADMIN_SIGNUPS.TOAST.LOAD_FAILED'));
    } finally {
      setDemosLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setQ(search.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [search]);

  useEffect(() => {
    if (tab === 'demos') loadDemos();
    else loadRows();
  }, [tab, loadRows, loadDemos]);

  const setTab = (next: Tab) => {
    const p = new URLSearchParams(params);
    if (next === 'review') p.delete('tab');
    else p.set('tab', next);
    setParams(p, { replace: true });
    setPage(0);
  };

  const openRow = (row: SignupRow) => {
    if (row.kind === 'visitor' || !row.vendorId) {
      setVisitor(row);
      return;
    }
    const p = new URLSearchParams(params);
    p.set('review', row.vendorId);
    setParams(p);
  };

  const openAccount = (vendorId: string) => {
    const p = new URLSearchParams(params);
    p.set('review', vendorId);
    setParams(p);
  };

  const closeSheet = () => {
    setVisitor(null);
    if (params.has('review')) {
      const p = new URLSearchParams(params);
      p.delete('review');
      setParams(p, { replace: true });
    }
  };

  const onChanged = (detail: SignupDetail | null) => {
    if (detail) setRows((rs) => rs.map((r) => (r.id === detail.id || r.vendorId === detail.vendorId ? { ...r, ...detail } : r)));
    loadStats();
    // Approved or blocked accounts leave "Needs review".
    if (tab === 'review') loadRows();
  };

  const checkSdk = async (row: SignupRow) => {
    if (!row.vendorId) return;
    const id = row.vendorId;
    setChecking((ids) => [...ids, id]);
    try {
      const detail = await runSdkCheck(id);
      if (detail) setRows((rs) => rs.map((r) => (r.vendorId === id ? { ...r, sdkPresence: detail.sdkPresence ?? r.sdkPresence } : r)));
    } finally {
      setChecking((ids) => ids.filter((x) => x !== id));
    }
  };

  const updateDemo = async (request: DemoRequest, patch: { status?: DemoStatus; note?: string }) => {
    const id = demoId(request);
    const before = demos;
    setDemos((ds) => ds.map((d) => (demoId(d) === id ? { ...d, ...patch } : d)));
    try {
      await api.updateDemoRequest(id, patch);
      toast.success(t('ADMIN_SIGNUPS.TOAST.DEMO_UPDATED'));
      if (patch.status) loadStats();
    } catch {
      setDemos(before);
      toast.error(t('ADMIN_SIGNUPS.TOAST.DEMO_UPDATE_FAILED'));
    }
  };

  const changePageSize = (size: number) => {
    setPageSize(size);
    setPage(0);
  };

  const shownDemos = useMemo(() => {
    const needle = q.toLowerCase();
    if (!needle) return demos;
    return demos.filter((d) => [d.name, d.email, d.company, d.website].some((v) => String(v || '').toLowerCase().includes(needle)));
  }, [demos, q]);

  const tabs = [
    { value: 'review' as Tab, label: t('ADMIN_SIGNUPS.TABS.REVIEW'), count: stats?.pendingReview },
    { value: 'all' as Tab, label: t('ADMIN_SIGNUPS.TABS.ALL') },
    { value: 'visitors' as Tab, label: t('ADMIN_SIGNUPS.TABS.VISITORS') },
    { value: 'demos' as Tab, label: t('ADMIN_SIGNUPS.TABS.DEMOS'), count: stats?.demoRequestsNew },
  ];

  const empty =
    q !== ''
      ? { icon: <SearchX />, title: t('ADMIN_SIGNUPS.EMPTY.SEARCH_TITLE'), body: t('ADMIN_SIGNUPS.EMPTY.SEARCH_BODY') }
      : tab === 'review'
        ? { icon: <Inbox />, title: t('ADMIN_SIGNUPS.EMPTY.REVIEW_TITLE'), body: t('ADMIN_SIGNUPS.EMPTY.REVIEW_BODY') }
        : { icon: <UserPlus />, title: t('ADMIN_SIGNUPS.EMPTY.ALL_TITLE'), body: t('ADMIN_SIGNUPS.EMPTY.ALL_BODY') };

  return (
    <AdminPage title={t('ADMIN_SIGNUPS.NAV')} parents={[{ label: t('ADMIN_SHELL.NAV.SECTION_CUSTOMERS') }]} description={t('ADMIN_SIGNUPS.DESCRIPTION')}>
      <SignupFunnel stats={stats} loading={statsLoading} />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <PillTabs value={tab} options={tabs} onChange={setTab} ariaLabel={t('ADMIN_SIGNUPS.TABS.LABEL')} />
        <SearchField value={search} onChange={setSearch} placeholder={t('ADMIN_SIGNUPS.SEARCH')} />
      </div>

      <Card className="gap-0 overflow-hidden p-0">
        {tab === 'demos' ? (
          <>
            <DemoRequestsTable rows={shownDemos} loading={demosLoading} onUpdate={updateDemo} onOpenAccount={openAccount} />
            {demosTotal > pageSize && (
              <TablePagination page={page} pageSize={pageSize} total={demosTotal} onPageChange={setPage} onPageSizeChange={changePageSize} />
            )}
          </>
        ) : loading && !rows.length ? (
          <div className="grid gap-2 p-4" data-testid="signups-loading">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-11 w-full" />
            ))}
          </div>
        ) : !rows.length ? (
          <EmptyState icon={empty.icon} title={empty.title} description={empty.body} />
        ) : (
          <>
            <Table data-testid="signups-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="ps-4">{t('ADMIN_SIGNUPS.TABLE.WEBSITE')}</TableHead>
                  <TableHead>{t('ADMIN_SIGNUPS.TABLE.CONTACT')}</TableHead>
                  <TableHead>{t('ADMIN_SIGNUPS.TABLE.PROGRESS')}</TableHead>
                  <TableHead>{t('ADMIN_SIGNUPS.TABLE.VERDICT')}</TableHead>
                  <TableHead>{t('ADMIN_SIGNUPS.TABLE.SDK')}</TableHead>
                  <TableHead>{t('ADMIN_SIGNUPS.TABLE.SIGNED_UP')}</TableHead>
                  <TableHead className="pe-4 text-end">
                    <span className="sr-only">{t('ADMIN_SIGNUPS.TABLE.ACTIONS')}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} className="cursor-pointer" onClick={() => openRow(row)} data-testid={`signup-row-${row.id}`}>
                    <TableCell className="max-w-[260px] ps-4">
                      <div className="truncate font-medium" dir="ltr">
                        {row.domain || t('ADMIN_SIGNUPS.TABLE.NO_DOMAIN')}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {[row.title, row.platform].filter(Boolean).join(' · ') ||
                          (row.kind === 'visitor' ? t('ADMIN_SIGNUPS.TABLE.VISITOR_CHECKS', { count: row.websiteChecks ?? 1 }) : '—')}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[240px]">
                      {row.email ? (
                        <>
                          <div className="truncate" dir="ltr">
                            {row.email}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">{row.name || '—'}</div>
                        </>
                      ) : (
                        <span className="text-xs text-muted-foreground">{t('ADMIN_SIGNUPS.TABLE.NO_ACCOUNT')}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <SignupProgress row={row} />
                        <SignupStatusBadge status={row.status} className="text-muted-foreground" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <VerdictChip verdict={row.verdict} />
                    </TableCell>
                    <TableCell data-testid={`signup-sdk-${row.id}`}>
                      {row.kind === 'account' && row.vendorId ? (
                        <SdkPresenceSummary
                          presence={row.sdkPresence}
                          checking={checking.includes(row.vendorId)}
                          onCheck={() => checkSdk(row)}
                          testId={`signup-sdk-check-${row.id}`}
                        />
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <RelativeTime value={row.createdAt} className="text-muted-foreground" />
                    </TableCell>
                    <TableCell className="pe-4 text-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openRow(row);
                        }}
                        data-testid={`signup-open-${row.id}`}
                      >
                        {row.status === 'pending_review' ? t('ADMIN_SIGNUPS.TABLE.REVIEW') : t('ADMIN_SIGNUPS.TABLE.OPEN')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={changePageSize} />
          </>
        )}
      </Card>

      <SignupDetailSheet
        vendorId={reviewId}
        row={reviewId ? rows.find((r) => r.vendorId === reviewId) ?? null : visitor}
        onClose={closeSheet}
        onChanged={onChanged}
      />
    </AdminPage>
  );
}

export default SignupsPage;
